export type ShareContext = 'search' | 'car' | 'news' | 'findcar' | 'listing' | 'compare' | 'generic';

export type ShareChannel =
  | 'copy'
  | 'native'
  | 'messenger'
  | 'whatsapp'
  | 'sms'
  | 'email'
  | 'twitter'
  | 'facebook'
  | 'linkedin';

export interface ShareData {
  title: string;
  text?: string;
  url?: string;
  context?: ShareContext;
}

/**
 * Bygger en URL med UTM-parametre for at kunne måle deling.
 * Bevarer eksisterende query-params (vigtigt for filtre/quiz-state).
 */
export function buildShareUrl(rawUrl: string, channel: ShareChannel): string {
  const CANONICAL_ORIGIN = 'https://minnyeelbil.dk';
  try {
    const u = new URL(rawUrl, CANONICAL_ORIGIN);
    // Tving altid det kanoniske produktionsdomæne (preview/staging skal ikke deles)
    const canonical = new URL(CANONICAL_ORIGIN);
    u.protocol = canonical.protocol;
    u.host = canonical.host;
    u.port = canonical.port;
    // Fjern interne Lovable-parametre der ikke skal med ud
    u.searchParams.delete('__lovable_token');
    u.searchParams.delete('__lovable_load_id');
    // Sæt UTM kun hvis ikke allerede sat (undgå at overskrive eksisterende kampagner)
    if (!u.searchParams.has('utm_source')) u.searchParams.set('utm_source', 'share');
    if (!u.searchParams.has('utm_medium')) u.searchParams.set('utm_medium', channel);
    return u.toString();
  } catch {
    return rawUrl;
  }
}

export function isNativeShareSupported(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (!('share' in navigator)) return false;
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

export function defaultShareText(context: ShareContext, title: string): string {
  switch (context) {
    case 'search':
      return `Se mit bil-udvalg på Min Nye Elbil: ${title}`;
    case 'findcar':
      return `Mit elbil-match fra Min Nye Elbil: ${title}`;
    case 'car':
      return `${title} — se priser og data på Min Nye Elbil`;
    case 'news':
      return `${title} — læs på Min Nye Elbil`;
    case 'compare':
      return `Sammenligning af elbiler: ${title}`;
    case 'listing':
      return `${title} — set på Min Nye Elbil`;
    default:
      return title;
  }
}

export function buildChannelHref(channel: ShareChannel, data: { title: string; text: string; url: string }): string {
  const u = encodeURIComponent(data.url);
  const t = encodeURIComponent(data.text);
  const title = encodeURIComponent(data.title);
  const body = encodeURIComponent(`${data.text}\n\n${data.url}`);

  switch (channel) {
    case 'whatsapp':
      return `https://wa.me/?text=${encodeURIComponent(`${data.text} ${data.url}`)}`;
    case 'messenger':
      // App-link med web fallback håndteres af browseren
      return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case 'sms':
      return `sms:?&body=${encodeURIComponent(`${data.text} ${data.url}`)}`;
    case 'email':
      return `mailto:?subject=${title}&body=${body}`;
    case 'twitter':
      return `https://twitter.com/intent/tweet?text=${t}&url=${u}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    default:
      return data.url;
  }
}
